<template>
	<div :class="{
		'searchResultContainer': true,
		'searchResultContainer--opened': isOpened
	}">
		<div class="searchResultContainer__header">
			<router-link
				:to="{
					query: {
						page: 'client',
						id: props.client.Id,
					}
				}"
				class="client-link"
			>
				<span v-html="client.Name" />
			</router-link>
			<span
				class="client-city"
				v-html="client.AddressChain?.[0]"
			/>
		</div>
		<div v-if="client.Clients" class="client-list">
			<template
				v-for="(c, index) in client.Clients.slice(0, 2)"
				:key="c.Id"
			>
				<router-link
					:to="{
					query: {
						page: 'client',
						id: c.Id,
					}
				}"
					class="client-list__item"
				>
					<span v-html="c.Name" />
				</router-link>
				<span
					v-if="index === 0 ? client.Clients.length > 1 : isExpanded"
					class='client-list__item-divider'
				>&nbsp;/ </span>
			</template>
			<span
				v-if="!isExpanded && client.Clients.length > 2"
				class="client-list__show-more"
				@click="isExpanded = !isExpanded"
			>
				{{ $t('more_count_label', { count: client.Clients.length - 2 }) }}
			</span>
			<template v-if="isExpanded">
				<template
					v-for="(c, index) in client.Clients.slice(2, client.Clients.length)"
					:key="index"
				>
					<router-link
						:to="{
							query: {
								page: 'client',
								id: c.Id,
							}
						}"
						class="client-list__item"
					>
						<span v-html="c.Name" />
					</router-link>
					<span
						v-if="index + 1 < client.Clients.slice(2, client.Clients.length).length"
						class='client-list__item-divider'>&nbsp;/ </span>
				</template>
			</template>
		</div>
		<div
			v-if="client.Parameters?.length === 1 ? !!client.Parameters[0].Instance?.Value : !!client.Parameters?.length"
			class="client-inn"
		>
			<template v-for="(param, index) in client.Parameters" :key="index">
				<template v-if="param.Instance && param.Instance.Value">
					<span class="client-inn__title">{{ param.Instance.Key }}: </span>
					<span v-html="param.Instance.Value"></span>
				</template>
			</template>
		</div>
		<div class="searchResultContainer__footer">
			<div class="client-tags">
				<div
					v-for="tag in client.Tags"
					:key="tag.Id"
					class="client-tag"
				>
					#<span v-html="tag.Name.toLowerCase()" />
				</div>

			</div>
			<div class="client-date">{{ formatDate(client.CreateTime) }}</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	defineProps,
	ref
} from "vue";
import {SearchClient} from "@/ api/SearchApi";
import {useRoute} from "vue-router";

const props = defineProps<{
	client: SearchClient;
	textFilter?: string;
}>();

const isExpanded = ref(false)
const route = useRoute()

const isOpened = computed(() =>
	route.query.page === 'client' && Number(route.query.id) === props.client.Id
)

function formatDate(date?: string | Date) {
	if (!date) return "";

	const dateObj = typeof date === "string" ? new Date(date) : date;
	return dateObj.toLocaleDateString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
	});
}
</script>

<style scoped lang="scss">
.searchResultContainer {
	padding: 4px;
	display: flex;
	flex-direction: column;

	&--opened {
		background-color: #F3F8F4;
		border-radius: 8px;
		animation: highlight-blink 2s ease-in-out;
	}

	&__header,
	&__footer {
		display: flex;
		justify-content: space-between;
	}

	&__header {
		align-items: flex-start;
		margin-bottom: 4px;
	}

	&__footer {
		align-items: center
	}
}

.client-link {
	color: #438451;
	font-size: 14px;
	line-height: 18px;
	text-decoration: none;
	font-weight: 500;

	&:hover {
		text-decoration: underline;
	}
}

.client-city {
	font-size: 14px;
	line-height: 18px;
	margin-left: 8px;
	color: #979797;
	text-align: right;
}

.client-list {
	display: inline;
	font-size: 12px;
	line-height: 16px;
	margin-bottom: 4px;
	
	&__item {
		text-decoration: none;
		line-height: 18px;
		color: #438451;
		cursor: pointer;

		&-divider {
			color: #4D4D4D;
			font-weight: 500;
		}

		&:hover {
			text-decoration: underline;
		}
	}

	&__show-more {
		display: inline-block;
		background-color: #F2F2F2;
		font-size: 12px;
		line-height: 16px;
		padding: 0 4px;
		border-radius: 8px;
		cursor: pointer;
		color: #979797;

		&:hover {
			background-color: #F3F8F4;
			color: #438451;
		}
	}
}

.client-inn {
	display: inline;
	color: #4D4D4D;
	margin-bottom: 4px;
	font-weight: 400;

	&__title {
		font-size: 14px;
		line-height: 18px;
		font-weight: 500;
	}
}

.client-tags {
	display: block;
	min-width: 0;
	overflow-wrap: anywhere;

	&__icon {
		margin-top: -1px;
	}
}

.client-tag {
	display: inline;
	margin-right: 4px;
	font-size: 14px;
	line-height: 18px;
	color: #979797;
}

.client-date {
	align-self: flex-end;
	flex-shrink: 0;
	color: #979797;
	font-size: 14px;
	line-height: 18px;
	text-align: right;
}

@keyframes highlight-blink {
	0%    { background-color: #F3F8F4; }
	16.6% { background-color: transparent; }
	33.3% { background-color: #F3F8F4; }
	50%   { background-color: transparent; }
	66.6% { background-color: #F3F8F4; }
	83.3% { background-color: transparent; }
	100%  { background-color: #F3F8F4; }
}

mark {
	background: #ffe7b2;
	border-radius: 2px;
	padding: 0 2px;
}
</style>
